/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "iOS SDK", description = "Hightouch's iOS SDK for collecting events", lastUpdatedDate = new Date(1696377600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "iOS SDK",
        description: "Hightouch's iOS SDK for collecting events",
        lastUpdatedDate: "Wed Oct 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "Initialization",
          "depth": 0,
          "id": "initialization"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }, {
          "value": "Reset",
          "depth": 1,
          "id": "reset"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      code: "code",
      strong: "strong",
      pre: "pre",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The iOS SDK makes it easy to track events from Swift and Objective-C applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The iOS SDK is hosted on GitHub at\n", _jsx(_components.code, {
          children: "git@github.com:ht-sdks/events-sdk-swift.git"
        }), " under the name\n", _jsx(_components.code, {
          children: "events-sdk-swift"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You may install the SDK by adding it to your ", _jsx(_components.code, {
          children: "package.swift"
        }), " file, or adding it\nas a Package Dependency via Xcode."]
      }), "\n", _jsx(_components.h2, {
        id: "initialization",
        children: "Initialization"
      }), "\n", _jsx(_components.p, {
        children: "You should initialize the SDK in a central location and store it in a variable\nfor use across your application. For example, you may want to initialize it in\nyour app delegate."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Swift example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["import Hightouch\n\nlet ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "configuration"
          }), " = Configuration(writeKey: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_WRITE_KEY\""
          }), ")\n    .apiHost(\"us-east-1.hightouch-events.com/v1\")\n\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "analytics"
          }), " = Analytics(configuration: configuration)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Objective-C example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["@import Hightouch\n\nHTConfiguration *", _jsx(_components.span, {
            className: "hljs-attr",
            children: "config"
          }), " = [[HTConfiguration alloc] initWithWriteKey:@", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_WRITE_KEY\""
          }), "]", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "config.apiHost"
          }), " = ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"us-east-1.hightouch-events.com/v1\""
          }), _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "analytics"
          }), " = [[HTAnalytics alloc] initWithConfiguration: config]", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Configuration options"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "writeKey"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "Your Highotuch write key."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "apiHost"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The API host to send the tracked events to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushAt"
              })
            }), _jsx(_components.td, {
              children: "Int"
            }), _jsx(_components.td, {
              children: "The number of events to queue before sending events to the Hightouch API. Defaults to 20."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushInterval"
              })
            }), _jsx(_components.td, {
              children: "Int"
            }), _jsx(_components.td, {
              children: "The maximum amount of time (in seconds) to store events locally before forwarding to Hightouch. Defaults to 30 seconds."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackApplicationLifecycleEvents"
              })
            }), _jsx(_components.td, {
              children: "Bool"
            }), _jsxs(_components.td, {
              children: ["Whether to automatically track application lifecycle events (like opening the application). Defaults to ", _jsx(_components.code, {
                children: "true"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event. It accepts user id and trait\ninformation for the current user -- if you only have partial information, you\ncan just supply a single parameter."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-swift",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "<", _jsx(_components.span, {
            className: "hljs-type",
            children: "T"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Codable"
          }), ">(", _jsx(_components.span, {
            className: "hljs-params",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ", ", _jsx(_components.span, {
            className: "hljs-params",
            children: "traits"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "T"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "<", _jsx(_components.span, {
            className: "hljs-type",
            children: "T"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Codable"
          }), ">(", _jsx(_components.span, {
            className: "hljs-params",
            children: "traits"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "T"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Codable"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event. The event name is required, and\nadditional properties that describe the event may be supplied."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-swift",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "<", _jsx(_components.span, {
            className: "hljs-type",
            children: "P"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Codable"
          }), ">(", _jsx(_components.span, {
            className: "hljs-params",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ", ", _jsx(_components.span, {
            className: "hljs-params",
            children: "properties"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "P"
          }), "?)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Codable"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event. The screen title is required, and\nadditional information about the screen's category and properties may be\nsupplied."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-swift",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "screen"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "title"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ", ", _jsx(_components.span, {
            className: "hljs-params",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), "? ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "nil"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "screen"
          }), "<", _jsx(_components.span, {
            className: "hljs-type",
            children: "P"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Codable"
          }), ">(", _jsx(_components.span, {
            className: "hljs-params",
            children: "title"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ", ", _jsx(_components.span, {
            className: "hljs-params",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), "? ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "nil"
          }), ", ", _jsx(_components.span, {
            className: "hljs-params",
            children: "properties"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "P"
          }), "?)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "title"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Codable"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "from_link"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsx(_components.p, {
        children: "The id identifying the user's group is required. Additional traits on the group\ncan be provided."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-swift",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "group"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "groupId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "func"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "group"
          }), "<", _jsx(_components.span, {
            className: "hljs-type",
            children: "T"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "Codable"
          }), ">(", _jsx(_components.span, {
            className: "hljs-params",
            children: "groupId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "String"
          }), ", ", _jsx(_components.span, {
            className: "hljs-params",
            children: "traits"
          }), ": ", _jsx(_components.span, {
            className: "hljs-type",
            children: "T"
          }), "?)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Codable"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The iOS SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush"
        }), "\nmethod."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-go",
          children: [_jsxs(_components.span, {
            className: "hljs-function",
            children: [_jsx(_components.span, {
              className: "hljs-keyword",
              children: "func"
            }), " ", _jsx(_components.span, {
              className: "hljs-title",
              children: "flush"
            }), _jsx(_components.span, {
              className: "hljs-params",
              children: "()"
            })]
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "reset",
        children: "Reset"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method resets the ", _jsx(_components.code, {
          children: "identify"
        }), " and ", _jsx(_components.code, {
          children: "group"
        }), " for the local session.\nSpecifically, it resets the ", _jsx(_components.code, {
          children: "anonymousId"
        }), ", ", _jsx(_components.code, {
          children: "userId"
        }), ", ", _jsx(_components.code, {
          children: "referrer"
        }), ", and ", _jsx(_components.code, {
          children: "traits"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method should be called when users log out. This way, if the user\nlogs back in with another account, the userIds and traits from the different\nsessions remain separate."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-go",
          children: [_jsxs(_components.span, {
            className: "hljs-function",
            children: [_jsx(_components.span, {
              className: "hljs-keyword",
              children: "func"
            }), " ", _jsx(_components.span, {
              className: "hljs-title",
              children: "reset"
            }), _jsx(_components.span, {
              className: "hljs-params",
              children: "()"
            })]
          }), "\n"]
        })
      })]
    });
  }
}
export default MDXContent;
